<template>
    <div>
        <div
            class="boxed:max-w-[347px] boxed:min-w-[347px] hidden shrink-0 overflow-hidden pb-32 lg:block lg:max-w-[280px] lg:min-w-[280px]">
            <slot :filterProducts="filterProducts" :currentFilters="currentFilters" :filters="orderedFilters">
                <transition name="fade">
                    <div>
                        <!-- Category tree - rendered independently of filter loading state -->
                        <BlockProductListingFiltersSearchCategoryTree
                            v-if="searchCategoryTree"
                            :tree="searchCategoryTree"
                            :is-open="true" />
                        <BlockProductListingFiltersSearchCategoryTree
                            v-else-if="manufacturerCategoryTree?.items?.length"
                            :tree="manufacturerCategoryTree"
                            :seo-url-map="manufacturerSeoUrlMap"
                            :is-manufacturer-page="true"
                            :is-open="true" />
                        <BlockProductListingFiltersCategoryTree
                            v-else-if="showShopwareTree"
                            :categories="categorySiblings"
                            :current-category-id="currentCategoryId"
                            :is-open="true" />
                        <!-- Skeleton while siblings load OR while Tweakwise hasn't settled yet -->
                        <div v-else-if="showTreeSkeleton" class="mb-6 animate-pulse pb-4">
                            <div
                                class="border-green-550 mb-6 flex w-full items-center justify-between border-b-2 px-4 pb-3">
                                <div class="h-[19px] w-24 rounded bg-green-300"></div>
                                <div class="size-3 rounded bg-green-300"></div>
                            </div>
                            <div class="space-y-4 pl-4">
                                <div class="h-4 w-28 rounded bg-green-300"></div>
                                <div class="h-4 w-20 rounded bg-green-300"></div>
                                <div class="h-4 w-32 rounded bg-green-300"></div>
                                <div class="h-4 w-24 rounded bg-green-300"></div>
                                <div class="h-4 w-16 rounded bg-green-300"></div>
                                <div class="h-4 w-28 rounded bg-green-300"></div>
                            </div>
                        </div>

                        <!-- Filters - gated by loading state -->
                        <template v-if="!isLoading">
                            <template v-for="(filter, index) in orderedFilters" :key="getFilterKey(filter)">
                                <BlockProductListingFiltersManufacturer
                                    v-if="isManufacturerFilter(filter)"
                                    @invokeFilter="(id, idx) => filterProducts(id, idx)"
                                    v-model:isOpen="filterOpenState[getFilterKey(filter)]"
                                    :currentFilters="currentFilters"
                                    :index="index"
                                    :code="filter.code"
                                    :label="'Merk'"
                                    :entities="filter?.entities"
                                    :options="filter?.options" />
                                <BlockProductListingFiltersPrice
                                    v-else-if="isPriceFilter(filter)"
                                    v-model:isOpen="filterOpenState[getFilterKey(filter)]"
                                    :index="index"
                                    :label="filter.label"
                                    :min="getPriceMinFromOptions(filter)"
                                    :max="getPriceMaxFromOptions(filter)" />
                                <BlockProductListingFiltersFilter
                                    v-else-if="filter.options"
                                    @invokeFilter="filterProducts"
                                    v-model:isOpen="filterOpenState[getFilterKey(filter)]"
                                    :currentFilters="currentFilters"
                                    :index="index"
                                    :code="filter.code"
                                    :filter="filter"
                                    :label="filter.label"
                                    :options="filter.options" />
                            </template>
                        </template>
                        <template v-else>
                            <div class="hidden w-[280px] animate-pulse lg:block">
                                <BlockProductListingFiltersSkeleton />
                            </div>
                        </template>
                    </div>
                </transition>
            </slot>
        </div>
        <BlockProductListingFiltersMobile
            @invokeFilter="filterProducts"
            :orderedFilters="orderedFilters"
            :total="total"
            :currentFilters="currentFilters"
            :sorts="sorts"
            :currentSort="currentSort"
            :categorySiblings="categorySiblings"
            :currentCategoryId="currentCategoryId"
            :searchCategoryTree="searchCategoryTree"
            :isManufacturerPage="isManufacturerPage"
            :manufacturerCategoryTree="manufacturerCategoryTree"
            :manufacturerSeoUrlMap="manufacturerSeoUrlMap"
            :treeSettled="treeSettled" />
    </div>
</template>

<script setup lang="ts">
    import type { PropType } from 'vue';
    import {
        isManufacturerFilter,
        isPriceFilter,
        getFilterKey,
        getAllFilterItems,
        getPriceMinFromOptions,
        getPriceMaxFromOptions,
        reorderFilters,
        parsePropertyIds,
        togglePropertyId,
        type PlpFilter as Filter,
    } from '~/utils/plp';

    interface Category {
        id: string;
        parentId?: string;
        active?: boolean;
        translated: {
            name: string;
        };
        seoUrls: Array<{ seoPathInfo: string }>;
        children?: Category[];
    }

    const props = defineProps({
        setFilters: Function,
        search: Function,
        filters: Array as PropType<Filter[]>,
        currentFilters: Object,
        sorts: Object,
        currentSort: String,
        total: Number,
        loading: Boolean,
        categorySiblings: Array as PropType<Category[]>,
        siblingsLoading: Boolean,
        currentCategoryId: String,
        searchCategoryTree: Object as PropType<any>,
        pendingAutoSelect: Boolean,
        isManufacturerPage: Boolean,
        manufacturerCategoryTree: Object as PropType<any>,
        manufacturerSeoUrlMap: Object as PropType<Record<string, string>>,
        // False until the first Tweakwise search for this mounted PLP has settled.
        treeSettled: Boolean,
    });

    // Tweakwise is the source of truth for the category tree. The Shopware sibling tree is
    // only a fallback for plain category PLPs — never on manufacturer pages, and never
    // before Tweakwise has settled (or, on error, stopped loading). Painting it early with
    // no Tweakwise filter yet is what flashed the whole untrimmed tree on navigation.
    const treeReady = computed(() => props.treeSettled || !props.loading);
    const showShopwareTree = computed(
        () => !props.isManufacturerPage && treeReady.value && (props.categorySiblings?.length ?? 0) > 0,
    );
    const showTreeSkeleton = computed(() => {
        if (props.searchCategoryTree || props.manufacturerCategoryTree?.items?.length) return false;
        if (showShopwareTree.value) return false;
        return props.siblingsLoading || (!props.isManufacturerPage && !treeReady.value);
    });

    const router = useRouter();
    const route = useRoute();
    const manufacturerId = inject<string | undefined>('manufacturerId', undefined);

    // Read active property IDs directly from the URL (single source of truth).
    const activePropertyIds = computed(() => parsePropertyIds(route.query.properties));

    // Detect the default brand hex ID on brand pages.
    // The backend sets nd_tweakwise_isselected=true on the brand's facet option.
    // The default brand is the one marked as "isselected" by Tweakwise but NOT explicitly in the URL.
    const defaultBrandHexId = computed(() => {
        if (!manufacturerId) return null;
        for (const filter of props.filters || []) {
            if (!isManufacturerFilter(filter)) continue;
            const defaultItem = getAllFilterItems(filter).find(
                item =>
                    item.customFields?.nd_tweakwise_isselected === true && !activePropertyIds.value.includes(item.id),
            );
            if (defaultItem) return defaultItem.id;
        }
        return null;
    });

    const filterProducts = (id: string, index: number) => {
        // Don't allow toggling the default brand on brand pages
        if (defaultBrandHexId.value && id === defaultBrandHexId.value) return;

        // Always read current state from the URL (single source of truth)
        const criteria = togglePropertyId(activePropertyIds.value, id);

        router.push({
            path: route.path,
            query: {
                ...route.query,
                properties: criteria.join('|') || undefined,
            },
        });
    };

    const isLoading = computed(() => {
        if (props.pendingAutoSelect) return true;
        // Show loading state only when loading is true AND there are no filters yet
        return props.loading && (!props.filters || props.filters.length === 0);
    });

    const orderedFilters = computed(() => reorderFilters(props.filters || []));

    // Track open/closed state per filter — persists across filter changes.
    // Keys come from getFilterKey (label-based, see ~/utils/plp).
    const filterOpenState = reactive<Record<string, boolean>>({});

    const defaultOpenLabels = ['merk', 'categorie', 'maat', 'kleur'];

    watch(
        orderedFilters,
        filters => {
            filters.forEach(filter => {
                const key = getFilterKey(filter);
                if (!(key in filterOpenState)) {
                    const label = isManufacturerFilter(filter) ? 'merk' : filter.label?.toLowerCase() || '';
                    filterOpenState[key] = defaultOpenLabels.includes(label);
                }
            });
        },
        { immediate: true },
    );
</script>
